/* Login auto-generated by npm run generator */
import { Button, Link, TextField, Typography } from "@material-ui/core"
import clsx from "clsx"
import PortalForm, { PortalSubmitButton } from "components/molecules/PortalForm"
import * as firebase from "firebase/app"
import "firebase/auth"
import { observable } from "mobx"
import { observer } from "mobx-react"
import React from "react"
import ReactInputMask from "react-input-mask"
import { Link as RouterLink, useHistory, useRouteMatch } from "react-router-dom"
import { PATHS } from "Router"
import Store from "store/Store"
import { useRootClasses } from "style"

interface LoginProps {}

enum Stage {
    SIGN_IN,
    RE_SIGNUP,
    ENTER_PHONE,
    NOT_ROBOT,
    VERIFY_CODE,
    LOGGED_IN,
}

class LoginState {
    @observable
    componentName = "Login"

    @observable
    stage: Stage = Stage.SIGN_IN

    @observable
    email = ""

    @observable
    password = ""

    @observable
    phone = ""

    @observable
    code = ""

    @observable
    reinvited?: boolean
}

const Login: React.FC<LoginProps> = (props) => {
    const history = useHistory()
    const rc = useRootClasses()
    const state = React.useRef(new LoginState()).current
    const match = useRouteMatch()

    const cancel = async () => {
        state.phone = ""
        state.code = ""
        state.email = ""
        state.password = ""
        state.stage = Stage.SIGN_IN
        await Store.auth.logout()
    }
    const { isLoggedIn, authUser, verifiedPhone } = Store.auth
    React.useEffect(() => {
        if (isLoggedIn) {
            state.stage = Stage.LOGGED_IN
            history.push(PATHS.HOME())
        }
        if (state.stage !== Stage.SIGN_IN && !authUser) {
            state.stage = Stage.SIGN_IN
        }
        if (state.stage !== Stage.RE_SIGNUP && authUser && !verifiedPhone) {
            state.stage = Stage.RE_SIGNUP
        }
        if (state.stage === Stage.RE_SIGNUP && authUser && verifiedPhone) {
            state.stage = Stage.ENTER_PHONE
        }
    }, [authUser, history, isLoggedIn, state.stage, verifiedPhone])

    const render = {
        [Stage.SIGN_IN]: {
            title: "Welcome Back!",
            subtitle: "Login to access your Qlarity Portal",
            form: (
                <>
                    <TextField label="Email" value={state.email} onChange={(e) => (state.email = e.target.value)} required autoFocus autoComplete="username" />
                    <TextField
                        label="Password"
                        type="password"
                        value={state.password}
                        onChange={(e) => (state.password = e.target.value)}
                        required
                        autoComplete="current-password"
                    />
                    <Link component={RouterLink} to={PATHS.ACCOUNT_HELP()} className={clsx(rc.colorText, rc.pt2)}>
                        Need help logging in to your account?
                    </Link>
                </>
            ),
            buttons: <PortalSubmitButton>Login</PortalSubmitButton>,
            action: async () => {
                await Store.auth.verifyEmailPassword(state.email, state.password)
                state.stage = Stage.ENTER_PHONE
            },
        },
        [Stage.RE_SIGNUP]: {
            title: "Uh oh...",
            subtitle:
                "It appears that your phone number for 2-factor authentication has not be completed. Please resend yourself an invite to complete the new user sign up.",
            form: (
                <>
                    <TextField label="Email" value={state.email} onChange={(e) => (state.email = e.target.value)} required autoFocus autoComplete="username" />
                </>
            ),
            buttons: (
                <>
                    {state.reinvited ? (
                        <Typography variant="h6">Please check your email for a fresh invite link</Typography>
                    ) : (
                        <PortalSubmitButton>Resend Invite</PortalSubmitButton>
                    )}
                    <Button onClick={cancel} variant="outlined" fullWidth>
                        Cancel
                    </Button>
                </>
            ),
            action: async () => {
                await firebase.auth().sendSignInLinkToEmail(state.email, {
                    url: `${window.location.origin}${match.path}`,
                    handleCodeInApp: true,
                })
                state.reinvited = true
            },
        },
        [Stage.ENTER_PHONE]: {
            title: "Verify Your Phone Number",
            subtitle: "Confirm and send an sms to your phone",
            form: (
                <>
                    <Typography>Number on record: (***) *** {Store.auth.verifiedPhone?.slice(-4)}</Typography>

                    <ReactInputMask mask="(999) 999 9999" value={state.phone} onChange={(e) => (state.phone = e.target.value)} autoFocus>
                        {() => <TextField label="Phone Number" type="tel" autoFocus />}
                    </ReactInputMask>
                </>
            ),
            buttons: (
                <>
                    <PortalSubmitButton>Send</PortalSubmitButton>
                    <Button onClick={cancel} variant="outlined" fullWidth>
                        Cancel
                    </Button>
                </>
            ),
            action: async () => {
                try {
                    state.stage = Stage.NOT_ROBOT
                    await Store.auth.sendSMS(state.phone, "firebase-captcha", () => (state.stage = Stage.VERIFY_CODE))
                } catch (e) {
                    state.stage = Stage.ENTER_PHONE
                    throw e
                }
            },
        },
        [Stage.NOT_ROBOT]: {
            title: "Prove Your Humanity",
            subtitle: "Out smart the robots!",
            form: (
                <>
                    <TextField label="Phone Number" value={state.phone} onChange={(e) => (state.phone = e.target.value)} />
                    <div id="firebase-captcha" className={clsx(rc.mt1, rc.mr1)} />
                </>
            ),
            buttons: (
                <>
                    <Button variant="outlined" fullWidth onClick={cancel}>
                        Cancel
                    </Button>
                </>
            ),
            action: async () => {},
        },
        [Stage.VERIFY_CODE]: {
            title: "Enter code",
            subtitle: "Enter in the 6 digit code",
            form: <TextField label="6-digit Code" value={state.code} onChange={(e) => (state.code = e.target.value)} autoFocus />,
            buttons: (
                <>
                    <PortalSubmitButton disabled={state.code.length !== 6}>Verify</PortalSubmitButton>
                    <Button onClick={cancel} variant="outlined" fullWidth>
                        Cancel
                    </Button>
                </>
            ),
            action: async () => {
                await Store.auth.verifyCodeAndLogin(state.code)
                state.stage = Stage.LOGGED_IN
            },
        },
        [Stage.LOGGED_IN]: {
            title: "Success!",
            subtitle: "Hooray!",
            form: <></>,
            buttons: <></>,
            action: async () => {},
        },
    }[state.stage]

    return <PortalForm title={render.title} subtitle={render.subtitle} form={render.form} buttons={render.buttons} buttonAction={render.action} />
}

export default observer(Login)
